import {AsyncRequestTypeName, SessionScreenshotSubject} from 'interfaces';

export enum PreSessionStepType {
    AsyncRequest = 'async-request',
    Screenshot = 'screenshot',
    Form = 'form',
    FileUpload = 'file-upload',
}

export enum PreSessionFlowCompletion {
    Escalate = 'escalate',
    End = 'end',
    // Retained for saved configurations; completion now ends the session.
    Chat = 'chat',
}

export enum PreSessionFileSide {
    Front = 'front',
    Back = 'back',
    Single = 'single',
}

export enum PreSessionStepStatus {
    Pending = 'pending',
    InProgress = 'in-progress',
    Done = 'done',
    Failed = 'failed',
    SkippedAfterRetries = 'skipped-after-retries',
}

export enum PreSessionFormFieldInputType {
    Text = 'text',
    Email = 'email',
    Phone = 'phone',
    Select = 'select',
    Textarea = 'textarea',
}

// Keep in sync with backend INTAKE/PRE_SESSION_FLOW_SUPPORTED_ASYNC_REQUESTS
export const PRE_SESSION_FLOW_SUPPORTED_ASYNC_REQUESTS = [
    AsyncRequestTypeName.Geolocation,
    AsyncRequestTypeName.SumaDocumentVerificationSDK,
] as const;

export type PreSessionFlowSupportedAsyncRequestTypeName = typeof PRE_SESSION_FLOW_SUPPORTED_ASYNC_REQUESTS[number];

export const PRE_SESSION_FLOW_SUPPORTED_SCREENSHOT_SUBJECTS = [
    SessionScreenshotSubject.ParticipantVideo,
    SessionScreenshotSubject.LocalScreen,
] as const;

export const PRE_SESSION_FLOW_LIMITS = {
    NAME_MAX: 200,
    PROMPT_MAX: 2000,
    STEPS_MAX: 20,
    PER_STEP_MAX_RETRIES: 3,
    CONTEXT_MAX_CHARS: 8000,
    FORM_FIELDS_MAX: 10,
    FORM_FIELD_LABEL_MAX: 200,
    FORM_FIELD_VALUE_MAX_DEFAULT: 500,
    FORM_FIELD_OPTIONS_MAX: 50,
    FORM_FIELD_OPTION_MAX: 200,
    SCREENSHOT_BASE64_MAX_BYTES: 5 * 1024 * 1024,
} as const;

export type PreSessionFormField = {
    id: string;
    label: string;
    inputType: PreSessionFormFieldInputType;
    required: boolean;
    options?: string[];
    maxLength?: number;
    title?: string;
};

export type PreSessionStep = {
    id: string;
    name: string;
    type: PreSessionStepType;
    required: boolean;
    clientPrompt: string;
    asyncRequest?: {
        typeName: PreSessionFlowSupportedAsyncRequestTypeName;
    };
    screenshot?: {
        // Configured screenshot name (matches an entry on Screenshots Management page).
        // Backend resolves it to `subject` at flow activation by looking up account/group screenshots.
        // Steps whose name does not resolve are pre-skipped (SkippedAfterRetries) on activation.
        name: string;
        subject?: typeof PRE_SESSION_FLOW_SUPPORTED_SCREENSHOT_SUBJECTS[number];
    };
    form?: {
        fields: PreSessionFormField[];
    };
    file?: {
        sides: PreSessionFileSide[];
        extensions?: string[];
        minBytes?: number;
        maxBytes?: number;
    };
};

/**
 * How the client may work through the steps. With both enabled they get a switch between the two;
 * with one, that is the only way offered. Absent means form-only.
 */
export enum PreSessionFlowMode {
    Chatbot = 'chatbot',
    Form = 'form',
}

export type PreSessionFlowModes = {
    chatbot: boolean;
    form: boolean;
};

export type PreSessionFlow = {
    enabled: boolean;
    steps: PreSessionStep[];
    onComplete?: PreSessionFlowCompletion;
    modes?: PreSessionFlowModes;
    defaultMode?: PreSessionFlowMode;
};

export type PreSessionFileRef = {
    side: PreSessionFileSide;
    fileName: string;
    ext: string;
    sizeBytes: number;
};

export type PreSessionStepCollected = {
    form?: Record<string, string>;
    screenshotRef?: string;
    asyncRequestRef?: string;
    files?: PreSessionFileRef[];
};

export type PreSessionFlowStepState = {
    stepId: string;
    status: PreSessionStepStatus;
    attempts: number;
    collected?: PreSessionStepCollected;
};

export type PreSessionFlowState = {
    activeMode?: PreSessionFlowMode;
    flowSnapshot: PreSessionFlow;
    currentStepIndex: number;
    steps: PreSessionFlowStepState[];
    awaitingConfirmation?: boolean;
    // Set when a flow answered by the chatbot has run out of steps and continues as a plain conversation.
    // There is no confirmation button in the chat, so it finishes on its own instead of awaiting one.
    completedInChat?: boolean;
};
